import { useMutation } from '@tanstack/react-query';
import { SKIP_UNAUTH_HEADER } from '@sentinel/api-client';
import { useApi } from '../../lib/api';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface ProfileError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): ProfileError {
  const e = new Error(stage) as ProfileError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

export function useUpdateName() {
  const api = useApi();
  return useMutation<void, ProfileError, { name: string }>({
    mutationFn: async ({ name }) => {
      const { error, response } = await api.PUT('/auth/profile', { body: { name } });
      if (error) throw toError('profile_name_update_failed', response, error);
    },
  });
}

export function useRequestEmailChange() {
  const api = useApi();
  return useMutation<void, ProfileError, { new_email: string }>({
    mutationFn: async ({ new_email }) => {
      const { error, response } = await api.PUT('/auth/profile/email', { body: { new_email } });
      if (error) throw toError('profile_email_change_failed', response, error);
    },
  });
}

export function useChangePassword() {
  const api = useApi();
  return useMutation<void, ProfileError, { current_password: string; new_password: string }>({
    mutationFn: async (body) => {
      // A 401 here means the current password was wrong, not that the session
      // expired. Without the opt-out, createUnauthMiddleware would read it as
      // expiry, fire sentinel:unauthenticated and bounce the user to /login for
      // a typo. Same reason reset-password/use-confirm.ts sets it.
      const { error, response } = await api.PUT('/auth/profile/password', {
        body,
        headers: { [SKIP_UNAUTH_HEADER]: '1' },
      });
      if (error) throw toError('profile_password_change_failed', response, error);
    },
  });
}
